import React, { useState } from "react";
import { Grid, Paper, IconButton, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import {
  Fullscreen,
  ZoomIn,
  ZoomOut,
  FullscreenExit,
} from "@mui/icons-material";
import styles from "./ImagePreviewSection.module.css";

const ImagePreviewSection = ({
  processedImage,
  backgroundColor,
  backgroundImage,
  applyFilters,
  previewRef,
  backgroundBlur,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const appliedFilters = applyFilters();

  const isGradient = (bg) => {
    return (
      bg?.startsWith("linear-gradient") ||
      bg?.startsWith("radial-gradient") ||
      bg?.startsWith("conic-gradient")
    );
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      previewRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  };

  return (
    <Grid item xs={12} md={8} className={styles.mainContainer}>
      <div className={styles.controlsContainer}>
        <Tooltip title="Zoom In" arrow>
          <IconButton
            onClick={handleZoomIn}
            className={styles.controlButton}
            size="small"
          >
            <ZoomIn sx={{ color: "#fff" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" arrow>
          <IconButton
            onClick={handleZoomOut}
            className={styles.controlButton}
            size="small"
          >
            <ZoomOut sx={{ color: "#fff" }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          arrow
        >
          <IconButton
            onClick={handleFullscreen}
            className={styles.controlButton}
            size="small"
          >
            {isFullscreen ? (
              <FullscreenExit sx={{ color: "#fff" }} />
            ) : (
              <Fullscreen sx={{ color: "#fff" }} />
            )}
          </IconButton>
        </Tooltip>
      </div>
      <Paper
        elevation={3}
        className={styles.paperContainer}
        sx={{
          backgroundColor: "#2d2d2d",
          borderRadius: "12px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={styles.imagePreviewWrapper}
          transition={{ duration: 0.3 }}
        >
          <div
            className={`${styles.imagePreview} ${
              isFullscreen ? styles.fullscreen : ""
            }`}
            ref={previewRef}
          >
            {/* Background layer with blur effect */}
            <div
              className={styles.backgroundLayer}
              style={{
                backgroundImage: backgroundImage
                  ? isGradient(backgroundImage)
                    ? backgroundImage
                    : `url(${backgroundImage})`
                  : "none",
                backgroundColor: backgroundColor,
                filter:
                  backgroundImage && !isGradient(backgroundImage)
                    ? `blur(${backgroundBlur}px)`
                    : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
            {/* Processed image with shadow and filters */}
            <img
              src={processedImage}
              alt="Processed"
              style={{
                ...appliedFilters,
                filter: `${appliedFilters.filter || ""} drop-shadow(4px 4px 12px rgba(0, 0, 0, 0.3))`,
                transform: `scale(${zoomLevel})`,
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className={styles.processedImage}
            />
            <div className={styles.previewOverlay} />
          </div>
        </motion.div>
      </Paper>
    </Grid>
  );
};

export default ImagePreviewSection;